import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Clock, CheckCircle, ArrowRight, Tag } from 'lucide-react';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Button } from '../components/ui/button';
import { api } from '../lib/auth';
import { LogoIcon } from '../components/Logo';

export default function ServiceDetailPage() {
  const { serviceId } = useParams();
  const navigate = useNavigate();
  const [service, setService] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadService();
  }, [serviceId]);

  const loadService = async () => {
    try {
      const response = await api.getService(serviceId);
      setService(response.data);
    } catch (error) {
      console.error('Failed to load service:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="max-w-4xl mx-auto px-4 py-8">
          <div className="h-96 bg-white/5 rounded-2xl animate-pulse" />
        </div>
      </DashboardLayout>
    );
  }

  if (!service) {
    return (
      <DashboardLayout>
        <div className="max-w-4xl mx-auto px-4 py-8 text-center">
          <LogoIcon size="lg" className="mx-auto mb-4 opacity-50" />
          <p className="text-gray-400">Service not found</p>
          <Link to="/marketplace">
            <Button className="btn-secondary mt-4">Back to Explore</Button>
          </Link>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Back Button */}
        <Link 
          to="/marketplace" 
          className="inline-flex items-center gap-2 text-gray-400 hover:text-white mb-6 transition-colors"
          data-testid="back-to-marketplace"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Explore
        </Link>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="glass rounded-2xl overflow-hidden"
        >
          {/* Header */}
          <div className="p-8 border-b border-white/10">
            <div className="flex flex-wrap items-start gap-4 mb-4">
              <span className="px-3 py-1 rounded-lg bg-white/5 text-sm text-gray-400">
                {service.department_name}
              </span>
              {service.covered_by_coop && (
                <span className="px-3 py-1 rounded-lg bg-accent/20 text-sm text-accent">
                  Co-Op Eligible
                </span>
              )}
            </div>
            
            <h1 className="font-heading text-3xl font-bold text-white mb-4">
              {service.title}
            </h1>
            
            <p className="text-lg text-gray-400">
              {service.short_description}
            </p>
          </div>

          {/* Details */}
          <div className="p-8 grid md:grid-cols-2 gap-8">
            <div>
              <h2 className="font-heading text-lg font-semibold text-white mb-4">What's Included</h2>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-400 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-300">Full service delivery by our expert team</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-400 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-300">Unlimited revisions during approval phase</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-400 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-300">Real-time progress updates and collaboration</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-400 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-300">AI-assisted project coordination</span>
                </li>
              </ul>
            </div>

            <div>
              <h2 className="font-heading text-lg font-semibold text-white mb-4">Service Details</h2>
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 rounded-xl bg-white/5">
                  <div className="flex items-center gap-3">
                    <Clock className="w-5 h-5 text-primary" />
                    <span className="text-gray-300">Estimated Delivery</span>
                  </div>
                  <span className="font-semibold text-white">{service.sla_estimate_days} days</span>
                </div>
                
                <div className="flex items-center justify-between p-4 rounded-xl bg-white/5">
                  <div className="flex items-center gap-3">
                    <Tag className="w-5 h-5 text-primary" />
                    <span className="text-gray-300">Pricing Model</span>
                  </div>
                  <span className="font-semibold text-white">Price on Demand</span>
                </div>
              </div>

              {service.tags && service.tags.length > 0 && (
                <div className="mt-6">
                  <h3 className="text-sm text-gray-400 mb-2">Tags</h3>
                  <div className="flex flex-wrap gap-2">
                    {service.tags.map((tag, i) => (
                      <span key={i} className="px-3 py-1 rounded-full bg-white/5 text-sm text-gray-300">
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* CTA */}
          <div className="p-8 bg-gradient-to-r from-primary/10 to-secondary/10 border-t border-white/10">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <div>
                <h3 className="font-heading text-xl font-semibold text-white mb-1">
                  Ready to start this mission?
                </h3>
                <p className="text-gray-400">Build your mission brief in under 60 seconds</p>
              </div>
              <Button 
                onClick={() => navigate(`/order/${service.id}`)}
                className="btn-primary text-lg px-8"
                data-testid="start-order-btn"
              >
                Start Order <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </div>
          </div>
        </motion.div>

        {/* How It Works */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="mt-8"
        >
          <h2 className="font-heading text-xl font-semibold text-white mb-4">How It Works</h2>
          <div className="grid sm:grid-cols-4 gap-4">
            {[
              { step: 1, title: 'Build Brief', desc: 'Answer a few questions' },
              { step: 2, title: 'Submit', desc: 'We receive your order' },
              { step: 3, title: 'Proposal', desc: 'Approve pricing' },
              { step: 4, title: 'Delivery', desc: 'Review & approve' },
            ].map((item) => (
              <div key={item.step} className="glass rounded-xl p-4 text-center">
                <div className="text-3xl font-heading font-bold text-primary/30 mb-2">{item.step}</div>
                <h4 className="font-semibold text-white">{item.title}</h4>
                <p className="text-sm text-gray-400">{item.desc}</p>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </DashboardLayout>
  );
}
